import { forwardRef, Suspense, useState, useCallback, useMemo, CSSProperties } from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Skeleton,
  useMantineTheme,
  useComputedColorScheme,
  Tooltip,
} from '@mantine/core';
import {
  IconRefresh,
  IconSettings,
  IconTrash,
  IconGripVertical,
} from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { getWidget } from '../registry';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { useDashboardContext } from '../context/DashboardProvider';
import { WidgetInstance, WidgetProps, DashboardGlobalData, WidgetSettings } from '../types';
import { pickTextColorForBackground } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores/preferencesStore';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Theme Styling Utilities
// ============================================================================

interface ThemeStyles {
  background?: string;
  color?: string;
  border?: string;
  boxShadow?: string;
}

/**
 * Get theme-specific styles based on widget settings
 */
function getThemeStyles(
  settings: WidgetSettings | undefined,
  colorScheme: 'light' | 'dark',
  primaryColor: string
): ThemeStyles {
  const themeVariant = settings?.themeVariant || 'default';

  switch (themeVariant) {
    case 'gradient': {
      // Use primary color for gradient
      const gradientFrom = primaryColor;
      const gradientTo = colorScheme === 'dark' ? '#1a1b1e' : '#f8f9fa';
      return {
        background: `linear-gradient(135deg, ${gradientFrom}22, ${gradientTo})`,
      };
    }
    case 'minimal': {
      return {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
      };
    }
    case 'custom': {
      const bgColor = settings?.backgroundColor;
      const gradientColor = settings?.backgroundGradient;

      if (!bgColor) return {};

      const background = gradientColor
        ? `linear-gradient(135deg, ${bgColor}, ${gradientColor})`
        : bgColor;

      const textColor =
        settings?.textColor === 'auto' ? pickTextColorForBackground(bgColor) : settings?.textColor;

      return {
        background,
        color: textColor,
      };
    }
    default:
      return {};
  }
}

// ============================================================================
// StatWidgetWrapper Component - Headerless wrapper for stat widgets
// ============================================================================

export interface StatWidgetWrapperProps {
  /** Widget instance data */
  instance: WidgetInstance;
  /** Global dashboard data */
  globalData?: DashboardGlobalData;
  /** Whether edit mode is active */
  isEditMode?: boolean;
  /** Whether this widget is being dragged */
  isDragging?: boolean;
  /** Drag start handler */
  onDragStart?: (e: React.DragEvent) => void;
  /** Drag end handler */
  onDragEnd?: () => void;
  /** Drag over handler */
  onDragOver?: (e: React.DragEvent) => void;
  /** Drop handler */
  onDrop?: (e: React.DragEvent) => void;
}

export const StatWidgetWrapper = forwardRef<HTMLDivElement, StatWidgetWrapperProps>(
  (
    {
      instance,
      globalData,
      isEditMode = false,
      isDragging = false,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDrop,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const widget = getWidget(instance.widgetId);
    const [isHovered, setIsHovered] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Get primary color from preferences store
    const colorSchemeData = usePreferencesStore((state) => state.getColorScheme());
    const primaryColor = colorSchemeData?.primary?.from || theme.primaryColor;
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Dashboard context for data refresh
    const dashboardContext = useDashboardContext();

    // Get theme-specific styles
    const themeStyles = useMemo(
      () => getThemeStyles(instance.settings, colorScheme, primaryColor),
      [instance.settings, colorScheme, primaryColor]
    );

    // Store actions
    const removeWidget = useDashboardStore((state) => state.removeWidget);
    const setWidgetFilters = useDashboardStore((state) => state.setWidgetFilters);
    const openSettingsModal = useDashboardStore((state) => state.openSettingsModal);

    // Refresh handler - triggers actual data refetch
    const handleRefresh = useCallback(() => {
      setRefreshKey((prev) => prev + 1);
      dashboardContext.refetch();
    }, [dashboardContext]);

    // Settings handler
    const handleSettings = useCallback(() => {
      openSettingsModal(instance.instanceId);
    }, [openSettingsModal, instance.instanceId]);

    // Remove handler
    const handleRemove = useCallback(() => {
      removeWidget(instance.instanceId);
    }, [removeWidget, instance.instanceId]);

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver?.(e);
      },
      [onDragOver]
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDrop?.(e);
      },
      [onDrop]
    );

    // Widget not found in registry
    if (!widget) {
      return (
        <Paper ref={ref} p="md" withBorder shadow="sm" style={{ height: '100%' }}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={handleRemove}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Box>
        </Paper>
      );
    }

    const WidgetComponent = widget.component;

    const widgetProps: WidgetProps = {
      widgetId: instance.widgetId,
      instanceId: instance.instanceId,
      isCollapsed: false, // Stat widgets don't collapse
      globalData,
      filters: instance.filters,
      onFilterChange: (filters) => setWidgetFilters(instance.instanceId, filters),
      settings: instance.settings,
    };

    // Build combined styles with theme overrides
    const paperStyle: CSSProperties = {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      cursor: isEditMode ? 'grab' : 'default',
      transition: 'box-shadow 0.2s ease, opacity 0.2s ease, transform 0.2s ease',
      opacity: isDragging ? 0.5 : 1,
      transform: isDragging ? 'scale(1.02)' : 'scale(1)',
      overflow: 'hidden',
      position: 'relative',
      // Apply theme styles
      ...(themeStyles.background && { background: themeStyles.background }),
      ...(themeStyles.color && { color: themeStyles.color }),
      ...(themeStyles.border && { border: themeStyles.border }),
      ...(themeStyles.boxShadow !== undefined && { boxShadow: themeStyles.boxShadow }),
    };

    // Determine if border should be shown (not for minimal theme)
    const showBorder = instance.settings?.themeVariant !== 'minimal';
    const showShadow = instance.settings?.themeVariant !== 'minimal';

    return (
      <Paper
        ref={ref}
        p={0}
        withBorder={showBorder}
        shadow={showShadow ? 'sm' : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        draggable={isEditMode}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={paperStyle}
      >
        {/* Hover Controls - Top Right */}
        {(isHovered || isEditMode) && (
          <Box
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 10,
              backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
              borderRadius: theme.radius.sm,
              boxShadow: theme.shadows.xs,
              padding: 2,
            }}
          >
            <Group gap={2}>
              {isEditMode && (
                <Tooltip label="Drag to reorder" withArrow>
                  <ActionIcon size="xs" variant="subtle" style={{ cursor: 'grab' }}>
                    <IconGripVertical size={12} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="Refresh" withArrow>
                <ActionIcon size="xs" variant="subtle" onClick={handleRefresh}>
                  <IconRefresh size={12} />
                </ActionIcon>
              </Tooltip>
              {widget.hasSettings !== false && (
                <Tooltip label="Settings" withArrow>
                  <ActionIcon size="xs" variant="subtle" onClick={handleSettings}>
                    <IconSettings size={12} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="Remove" withArrow>
                <ActionIcon size="xs" variant="subtle" color="red" onClick={handleRemove}>
                  <IconTrash size={12} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        )}

        {/* Widget Content - Full Area */}
        <Box
          p="sm"
          pb="md"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Suspense fallback={<StatWidgetLoader />}>
            <WidgetComponent key={refreshKey} {...widgetProps} />
          </Suspense>
        </Box>
      </Paper>
    );
  }
);

StatWidgetWrapper.displayName = 'StatWidgetWrapper';

// ============================================================================
// StatWidgetLoader Component
// ============================================================================

function StatWidgetLoader() {
  return (
    <Box style={{ textAlign: 'center', width: '100%' }}>
      <Skeleton height={32} width="40%" mx="auto" mb="xs" />
      <Skeleton height={16} width="60%" mx="auto" />
    </Box>
  );
}

export default StatWidgetWrapper;
